import type { HttpClient } from "../http/client";
import { saveResponse } from "../http/download";
import { readEventStream, type SseFrame } from "../http/sse";
import type {
  AdminSessionsResponse,
  AdmitRequest,
  AiStatus,
  ArrivalsResponse,
  AttendeeListFilter,
  AttendeesPage,
  ChatResponse,
  ChatTurn,
  CheckInResponse,
  Demographics,
  ExportFormat,
  FrontdeskState,
  Overview,
  RegistrationsResponse,
  ScanRequest,
  SearchQuery,
  SearchResponse,
  StaffIdentity,
  StaffLoginResponse,
  SyncItem,
  SyncResponse,
  WalkInRequest,
} from "../wire/staff";
import type { Lang } from "../wire/attendee";
import type {
  MerchRedeemRequest,
  MerchRedeemResponse,
  SurveyQuestionsRequest,
  SurveyQuestionsResponse,
  SurveyResponsesResponse,
  SurveySummaryResponse,
} from "../wire/survey";

/**
 * Staff sign-in, the door, the board, the dashboard and Kara (docs/api.md 38,
 * 42, 44). The role gate is the backend's; the console only routes on it.
 */
export function createStaffApi(http: HttpClient) {
  return {
    /* 38.2-38.3 */
    login: (email: string, password: string) =>
      http.json<StaffLoginResponse>("POST", "/staff/login", { body: { email, password }, anonymous: true }),
    logout: () => http.void("POST", "/staff/logout"),
    me: () => http.json<StaffIdentity>("GET", "/staff/me"),

    /* 38.4-38.8 The door: frontdesk, admin, superuser */
    scan: (body: ScanRequest) => http.json<CheckInResponse>("POST", "/checkins/scan", { body }),
    sync: (scans: SyncItem[]) => http.json<SyncResponse>("POST", "/checkins/sync", { body: { scans } }),
    admit: (body: AdmitRequest) => http.json<CheckInResponse>("POST", "/checkins/admit", { body }),
    walkIn: (body: WalkInRequest) => http.json<CheckInResponse>("POST", "/checkins/walk-in", { body }),
    /** 46.4 A merchandise voucher, scanned at the same door scanner. */
    redeemMerch: (body: MerchRedeemRequest) => http.json<MerchRedeemResponse>("POST", "/merch/redeem", { body }),
    search: (q: SearchQuery, signal?: AbortSignal) =>
      http.json<SearchResponse>("GET", "/attendees/search", { query: { ...q }, ...(signal ? { signal } : {}) }),

    /* 38.9 The board: monitor, admin, superuser */
    frontdeskState: () => http.json<FrontdeskState>("GET", "/frontdesk/state"),
    frontdeskStream: (onFrame: (frame: SseFrame) => void, signal: AbortSignal) =>
      readEventStream(http, "/frontdesk/stream", onFrame, signal),

    /* 42 The dashboard */
    overview: () => http.json<Overview>("GET", "/admin/overview"),
    arrivals: () => http.json<ArrivalsResponse>("GET", "/admin/arrivals"),
    registrations: () => http.json<RegistrationsResponse>("GET", "/admin/registrations"),
    adminSessions: () => http.json<AdminSessionsResponse>("GET", "/admin/sessions"),
    demographics: () => http.json<Demographics>("GET", "/admin/demographics"),
    attendees: (query: string, page: number, filter: AttendeeListFilter = {}) =>
      http.json<AttendeesPage>("GET", "/admin/attendees", { query: { query, page, ...filter } }),
    exportAttendees: async (format: ExportFormat) => {
      const res = await http.raw("GET", "/admin/export", { query: { format }, accept: "*/*", timeoutMs: 120_000 });
      return saveResponse(res, `attendees.${format}`);
    },
    /** 42.8 Delete an attendee: admins too, the same delete as the superuser's (45.10). */
    deleteAttendee: (accountId: string) => http.void("DELETE", `/admin/attendees/${encodeURIComponent(accountId)}`),
    /** 46.5 The survey's figures and per-question tallies; 46.7 every response with who gave it. */
    surveySummary: (lang: Lang) => http.json<SurveySummaryResponse>("GET", "/admin/survey", { query: { lang } }),
    surveyResponses: (lang: Lang) =>
      http.json<SurveyResponsesResponse>("GET", "/admin/survey/responses", { query: { lang } }),
    /** 46.5 The survey's responses alone, one row each. */
    exportSurvey: async (format: ExportFormat) => {
      const res = await http.raw("GET", "/admin/survey/export", { query: { format }, accept: "*/*", timeoutMs: 120_000 });
      return saveResponse(res, `survey.${format}`);
    },
    /** 46.6 The survey's questions: admins edit them from the console too. */
    surveyQuestions: () => http.json<SurveyQuestionsResponse>("GET", "/admin/survey/questions"),
    replaceSurveyQuestions: (body: SurveyQuestionsRequest) =>
      http.json<Pick<SurveyQuestionsResponse, "questions">>("PUT", "/admin/survey/questions", { body }),

    /* 44 Kara */
    aiStatus: () => http.json<AiStatus>("GET", "/admin/ai/status"),
    aiChat: (messages: ChatTurn[]) =>
      http.json<ChatResponse>("POST", "/admin/ai/chat", { body: { messages }, timeoutMs: 90_000 }),
  };
}

export type StaffApi = ReturnType<typeof createStaffApi>;
